import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewTag,
  fetchTagRegexes,
  setSelectedTagRegexesSet,
  updateTagRegexesAction,
  addNewCategoryGroup,
} from "../../../../redux/reducers/TagRegexes";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { ReduxState } from "../../../../redux";
import { flatten, groupBy } from "lodash";
import {
  Button,
  Row,
  Col,
  Modal,
  FormControl,
  InputGroup,
  Form,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  deleteTagRegexes,
  deleteTagRegexesSet,
  resetEditedCategories,
  resetEditedCategoriesTagLevels,
} from "../../../../docuclipper/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCustomUi } from "../../../Docuclipper/ConfirmAlert/ConfirmAlert";
import { confirmAlert } from "react-confirm-alert";
import CategoryTable from "./CategoryTable";
import { TagCategoryRule } from "src/docuclipper/DocuclipperTypes";
import {
  handleAddNewKeyword,
  extractKeywordsFromJsonRules,
} from "src/utils/categoryManagement";
import { getAdminTagRegexes } from "src/docuclipper/api";
import AlertBanner from "src/views/Docuclipper/AlertBanner/AlertBanner";

import { useHistory } from "react-router-dom";
import { createAlert } from "src/redux/reducers/Alerts";
import { categorizeActionAsync } from "../../../../redux/reducers/CategoriesAsync";

export const TagRegexes: React.FC = () => {
  const DEFAULT_SYSTEM_GROUP = "c89e2ac11db6184500f0933279a407b3";
  const [existingTag, setExistingTag] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [showAddNewRow, setShowAddNewRow] = React.useState(false);
  const [filterText, setFilterText] = React.useState("");
  const [showNewGroup, setShowNewGroup] = React.useState(false);
  const [groupName, setGroupName] = React.useState("");
  const [changesMade, setChangesMade] = React.useState(false);
  const [systemKeywords, setSystemKeywords] = React.useState<
    Record<string, string[]>
  >({});

  const { tagRegexes, tagRegexesSets, selectedTagRegexesSet } = useSelector(
    (state: ReduxState) => state.TagRegexes,
  );

  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(fetchTagRegexes("categorize"));
  }, []);
  const lsKey = "docuclipper.ui.selectedCategoryGroup";
  React.useEffect(() => {
    // Only run when nothing is selected but we have sets (e.g. initial load or after refetch)
    if (!selectedTagRegexesSet && tagRegexesSets.length > 0) {
      const savedSelection = localStorage.getItem(lsKey);
      const savedExistsInList = savedSelection
        ? tagRegexesSets.some((s) => s.name === savedSelection)
        : false;

      if (savedExistsInList) {
        // After first use: use saved selection from localStorage
        dispatch(setSelectedTagRegexesSet(savedSelection!));
      } else {
        // First use (no localStorage) or saved set was deleted: always pick something so we never have nothing selected
        const hasDefaultGroup = tagRegexesSets.some(
          (set) => set.name === DEFAULT_SYSTEM_GROUP,
        );
        const nameToSelect = hasDefaultGroup
          ? DEFAULT_SYSTEM_GROUP
          : tagRegexesSets[0].name;
        dispatch(setSelectedTagRegexesSet(nameToSelect));
        localStorage.setItem(lsKey, nameToSelect);
      }
    }
  }, [tagRegexesSets, selectedTagRegexesSet]);

  React.useEffect(() => {
    if (
      selectedTagRegexesSet &&
      selectedTagRegexesSet.name === DEFAULT_SYSTEM_GROUP
    ) {
      getAdminTagRegexes("categorize")
        .then(({ tagRegexes: adminTagRegexes }) => {
          const keywordsMap: Record<string, string[]> = {};
          (adminTagRegexes || []).forEach((tag) => {
            let positiveKeywords: string[] = [];
            if (tag?.jsonRules) {
              const extracted = extractKeywordsFromJsonRules(
                typeof tag.jsonRules === "string"
                  ? tag.jsonRules
                  : JSON.stringify(tag.jsonRules),
              );
              positiveKeywords = extracted.positiveKeywords || [];
            } else if (tag?.regexes) {
              try {
                positiveKeywords = JSON.parse(tag.regexes) || [];
              } catch (error) {
                positiveKeywords = [];
              }
            }

            if (positiveKeywords.length > 0) {
              keywordsMap[tag.tag] = positiveKeywords;
            }
          });
          setSystemKeywords(keywordsMap);
        })
        .catch(() => {
          setSystemKeywords({});
        });
    } else {
      setSystemKeywords({});
    }
  }, [selectedTagRegexesSet]);

  const names = Object.keys(groupBy(tagRegexesSets, "name"));
  const selectedTagIds = !selectedTagRegexesSet
    ? []
    : tagRegexesSets
        .filter((t) => t.name === selectedTagRegexesSet.name)
        .map((x) => x.tagRegexesId);

  const allTagIds = flatten(tagRegexesSets.map((x) => x.tagRegexesId));

  const getTagName = (id) => {
    const matches = tagRegexes.filter((x) => x.id === id);
    if (matches.length > 0) {
      return matches[0].tag;
    }
    return "default";
  };

  const getTagLabel = (id) => {
    const matches = tagRegexes.filter((x) => x.id === id);
    if (matches.length > 0) {
      return matches[0]?.tagLabel ? `(${matches[0]?.tagLabel})` : "";
    }
    return "";
  };

  const existingCategoriesOptions = allTagIds
    .filter((x) => !selectedTagIds.includes(x))
    .map((x) => {
      const setName =
        tagRegexesSets.find((set) => set.tagRegexesId.includes(x))?.name ||
        "unknown";
      return {
        label: `${setName} - ${getTagName(x)} ${getTagLabel(x)}`,
        value: x,
      };
    });

  const hideStr = (x: string) =>
    x === "c89e2ac11db6184500f0933279a407b3" ? "default" : x;

  const toggle = () => {
    // If closing modal and changes were made, trigger categorization
    if (show && changesMade) {
      setShow(false);
      setChangesMade(false);

      // Trigger categorization after a short delay
      setTimeout(() => {
        dispatch(categorizeActionAsync({ l1, l2 }));
        dispatch(
          createAlert({
            message: "Re-categorizing transactions with updated rules...",
            timeout: 3000,
            type: "info",
          }),
        );
      }, 300);
    } else {
      setShow(!show);
    }
  };

  const isDefault = selectedTagRegexesSet
    ? selectedTagRegexesSet.name === "c89e2ac11db6184500f0933279a407b3"
    : false;

  const onAddNewKeyword = (
    tagId: string,
    keyword: string,
    isPositive: boolean,
  ) => {
    handleAddNewKeyword(dispatch, tagRegexes, tagId, keyword, isPositive);
    setChangesMade(true);
  };

  const handleAddCategory = (
    tag: string,
    tagLabel: string,
    keywords: string[],
    negativeKeywords: string[],
  ) => {
    if (selectedTagRegexesSet) {
      const rule: TagCategoryRule = {
        keywords: keywords,
        negativeKeywords: negativeKeywords,
        tag: tag,
      };
      dispatch(
        addNewTag(selectedTagRegexesSet.name, tag, tagLabel, "[]", rule),
      );
      setShowAddNewRow(false);
      setChangesMade(true);
    }
  };

  const onRemoveKeyword = (
    tagId: string,
    keyword: string,
    isPositive: boolean,
  ) => {
    const tagToUpdate = tagRegexes.find((t) => t.id === tagId);
    if (tagToUpdate) {
      let positiveKeywords = [];
      let negativeKeywords = [];

      if (tagToUpdate.jsonRules) {
        const keywords = extractKeywordsFromJsonRules(tagToUpdate.jsonRules);
        positiveKeywords = keywords.positiveKeywords;
        negativeKeywords = keywords.negativeKeywords;
      } else if (tagToUpdate.regexes) {
        positiveKeywords = JSON.parse(tagToUpdate.regexes);
      }

      const updatedPositiveKeywords = isPositive
        ? positiveKeywords.filter((k) => k !== keyword)
        : positiveKeywords;
      const updatedNegativeKeywords = isPositive
        ? negativeKeywords
        : negativeKeywords.filter((k) => k !== keyword);
      const updatedRule = {
        keywords: updatedPositiveKeywords,
        negativeKeywords: updatedNegativeKeywords,
        tag: tagToUpdate.tag,
      };

      dispatch(
        updateTagRegexesAction(
          tagId,
          tagToUpdate.tag,
          tagToUpdate.tagLabel,
          JSON.stringify(updatedRule),
          updatedRule,
        ),
      );
      setChangesMade(true);
    }
  };

  const onDeleteTag = (id) => {
    deleteTagRegexes(id)
      .then(() => {
        dispatch(fetchTagRegexes("categorize"));
        setChangesMade(true);
      })
      .catch((err) =>
        dispatch(
          createAlert({
            message: err.message || "Error deleting",
            timeout: 0,
            type: "error",
          }),
        ),
      );
  };

  const onSaveTag = (
    tagId: string,
    newTag: string,
    newTagLabel: string,
    regexes: string,
    jsonRules: any,
  ) => {
    dispatch(
      updateTagRegexesAction(tagId, newTag, newTagLabel, regexes, jsonRules),
    );
    setChangesMade(true);
  };

  const onAddExistingCategoryToGroup = () => {
    if (existingTag && selectedTagRegexesSet) {
      const tag = tagRegexes.find((t) => t.id === existingTag);
      if (tag) {
        const keywords = extractKeywordsFromJsonRules(tag.jsonRules || "");
        dispatch(
          addNewTag(
            selectedTagRegexesSet.name,
            getTagName(existingTag),
            getTagLabel(existingTag).replace("(", "").replace(")", ""),
            tag.regexes,
            {
              keywords: keywords.positiveKeywords,
              negativeKeywords: keywords.negativeKeywords,
              tag: getTagName(existingTag),
            },
          ),
        );
        setChangesMade(true);
      }
    }
  };

  const filteredTags = tagRegexes.filter(
    (t) =>
      selectedTagIds.includes(t.id) &&
      (t.tag.toLowerCase().includes(filterText.toLowerCase()) ||
        t?.tagLabel?.toLowerCase().includes(filterText.toLowerCase())),
  );

  const history = useHistory();
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel,
  );
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedAccountReconciler } = useSelector(
    (state: ReduxState) => state.Reconciler,
  );
  const toggleAddNewCategoryGroup = () => setShowNewGroup(!showNewGroup);

  const handleCategoryGroupChange = (value: any) => {
    if (!value) return;

    confirmAlert({
      customUI: getCustomUi({
        onConfirm: async () => {
          try {
            // Reset all edited categories with deleteCategory=true
            if (l1 && l2) {
              await resetEditedCategoriesTagLevels(
                l1,
                l2,
                selectedAccountReconciler || "",
                true,
              );
            } else if (job && job.id) {
              await resetEditedCategories(
                job.id,
                selectedAccountReconciler || "",
                true,
              );
            }

            // Change category group
            dispatch(setSelectedTagRegexesSet(value.value));
            localStorage.setItem(lsKey, value.value);

            // Re-categorize with new category group
            dispatch(
              createAlert({
                message:
                  "Category group changed. Re-categorizing transactions...",
                timeout: 3000,
                type: "success",
              }),
            );

            // Auto-trigger categorization with the new category group
            setTimeout(() => {
              dispatch(categorizeActionAsync({ l1, l2 }));
            }, 500);
          } catch (error: any) {
            dispatch(
              createAlert({
                message: error.message || "Error resetting categories",
                timeout: 0,
                type: "error",
              }),
            );
          }
        },
        confirmCTA: "Yes, Reset & Switch",
        cancelCTA: "Cancel",
        getMessage: () => (
          <div style={{ marginBottom: "20px" }}>
            <h5>Switch Category Group?</h5>
            <p>
              Switching category groups will reset all manually edited
              categories and recategorize all transactions using the new
              category group's rules.
            </p>
            <p>
              <strong>This action cannot be undone.</strong>
            </p>
          </div>
        ),
      }),
    });
  };

  React.useEffect(() => {
    if (showNewGroup) {
      setGroupName("");
    }
  }, [showNewGroup]);

  const handleAddNewCategoryGroup = () => {
    const rule: TagCategoryRule = {
      keywords: [],
      negativeKeywords: [],
      tag: groupName.trim(),
    };
    dispatch(addNewCategoryGroup(groupName, rule));
    localStorage.setItem(lsKey, groupName);
    toggleAddNewCategoryGroup();
  };

  return (
    <div>
      <AlertBanner />
      <Row className="align-items-center">
        <Col>
          <span>Category Group</span>
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="category-group-tooltip">
                <div style={{ textAlign: "left" }}>
                  <strong>What are Category Groups?</strong>
                  <div className="mt-1">
                    Category groups are sets of categorization rules. Each group
                    contains categories with their keywords for automatic
                    transaction categorization.
                  </div>
                  <div className="mt-2">
                    <strong>Use cases:</strong>
                    <ul className="mb-0 pl-3">
                      <li>Different clients or projects</li>
                      <li>Personal vs business categorization</li>
                      <li>Testing new category structures</li>
                    </ul>
                  </div>
                </div>
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              icon="info-circle"
              className="ml-2 text-info"
              style={{ cursor: "help" }}
            />
          </OverlayTrigger>
          <Button
            className="categorization-tour-add-group"
            variant="link"
            onClick={toggleAddNewCategoryGroup}
          >
            <FontAwesomeIcon icon="plus" />
            <span className="ml-1" data-cy="add-new-category-group-link">
              Add New
            </span>
          </Button>
          <Select
            className="categorization-tour-select-group"
            data-cy="main-category-select"
            options={names.map((x) => ({
              label: hideStr(x),
              value: x,
            }))}
            value={
              selectedTagRegexesSet
                ? {
                    label: hideStr(selectedTagRegexesSet.name),
                    value: selectedTagRegexesSet.id as string,
                  }
                : null
            }
            onChange={handleCategoryGroupChange}
          />
        </Col>

        <Button
          className="mt-4 categorization-tour-edit-keywords"
          variant="outline-primary"
          onClick={toggle}
          data-cy="edit-categories-btn"
        >
          <FontAwesomeIcon icon="edit" className="mr-2" />
          Edit Categories & Keywords
        </Button>

        <Button
          className="mt-4 ml-2"
          variant="outline-primary"
          onClick={() => {
            const params = new URLSearchParams();
            if (l1 !== undefined && l1 !== null) params.set("l1", l1);
            if (l2 !== undefined && l2 !== null) params.set("l2", l2);
            if (selectedTagRegexesSet?.name)
              params.set("categoryGroup", selectedTagRegexesSet.name);

            history.push(`/extract/vendorscsv?${params.toString()}`);
          }}
        >
          <FontAwesomeIcon icon="upload" className="mr-2" />
          Upload CSV
        </Button>

        {!isDefault && (
          <Button
            className="mt-4 ml-2"
            variant="link"
            onClick={() => {
              confirmAlert({
                customUI: getCustomUi({
                  onConfirm: () => {
                    if (selectedTagRegexesSet) {
                      deleteTagRegexesSet(
                        selectedTagRegexesSet.name,
                        selectedTagRegexesSet.type,
                      )
                        .then((rsp) => {
                          dispatch(fetchTagRegexes("categorize"));
                          if (tagRegexesSets.length > 0) {
                            dispatch(
                              setSelectedTagRegexesSet(tagRegexesSets[0].name),
                            );
                          } else {
                            dispatch(setSelectedTagRegexesSet(""));
                          }
                        })
                        .catch((err) =>
                          dispatch(
                            createAlert({
                              message: err.message || "Error deleting",
                              timeout: 0,
                              type: "error",
                            }),
                          ),
                        );
                    }
                  },
                  confirmCTA: "Delete",
                  cancelCTA: "Cancel",
                  getMessage: () => (
                    <div
                      className="div--confirm-job-delete"
                      style={{ marginBottom: "20px" }}
                    >
                      <span>{`Are you sure you want to delete this category group?`}</span>
                    </div>
                  ),
                }),
              });
            }}
          >
            <FontAwesomeIcon icon="trash" />
            <span className="ml-1">Delete Group</span>
          </Button>
        )}

        <Modal
          show={show}
          onHide={toggle}
          size="xl"
          dialogClassName="modal-95w"
        >
          {/* @ts-ignore - react-bootstrap 1.6.1 type definition issue */}
          <Modal.Header closeButton>
            <Modal.Title>Edit Categories/Keywords</Modal.Title>
          </Modal.Header>
          <Modal.Body
            style={{ padding: "1.5rem", maxWidth: "100%", width: "100%" }}
          >
            <div className="mb-3">
              <AlertBanner />
            </div>
            {/* <Row>
              <Col md="4">
                <Row>
                  <Col>
                    <Row>
                      <div className="ml-2" style={{ width: "300px" }}>
                        <Select
                          data-cy="existing-category-select"
                          options={existingCategoriesOptions}
                          value={
                            existingTag
                              ? {
                                  label: `${getTagName(
                                    existingTag
                                  )} ${getTagLabel(existingTag)}`,
                                  value: existingTag,
                                }
                              : null
                          }
                          onChange={(value) => {
                            if (value) {
                              setExistingTag(value.value);
                            }
                          }}
                        />
                      </div>
                      <Button
                        variant="link"
                        onClick={onAddExistingCategoryToGroup}
                      >
                        <FontAwesomeIcon icon="plus" />
                        <span className="ml-1">
                          Add Existing Category to Group
                        </span>
                      </Button>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row> */}
            <div className="mb-3">
              <div className="mb-3">
                <InputGroup style={{ maxWidth: "400px" }}>
                  <InputGroup.Prepend>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon="search" className="text-muted" />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <FormControl
                    type="text"
                    placeholder="Search categories by name..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  {filterText && (
                    <InputGroup.Append>
                      <Button
                        variant="outline-secondary"
                        onClick={() => setFilterText("")}
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                    </InputGroup.Append>
                  )}
                </InputGroup>
                <div className="text-muted small mt-1">
                  {filteredTags.length}{" "}
                  {filteredTags.length === 1 ? "category" : "categories"}
                </div>
              </div>
              <div>
                <Button
                  variant="link"
                  onClick={() => setShowAddNewRow(true)}
                  disabled={showAddNewRow}
                  // size="lg"
                >
                  <FontAwesomeIcon icon="plus" />
                  <span className="ml-2">Add New Category</span>
                </Button>
              </div>
            </div>

            <CategoryTable
              tags={
                filteredTags.sort((a, b) =>
                  a.tag.localeCompare(b.tag),
                ) as unknown as {
                  id: string;
                  tag: string;
                  tagLabel?: string;
                  regexes: string;
                  jsonRules: TagCategoryRule;
                }[]
              }
              systemKeywords={systemKeywords}
              onDeleteTag={(id) => onDeleteTag(id)}
              onAddKeyword={(tagId, keyword, isPositive) =>
                onAddNewKeyword(tagId, keyword, isPositive)
              }
              onRemoveKeyword={onRemoveKeyword}
              onSaveTag={onSaveTag}
              onAddNewCategory={handleAddCategory}
              showAddNewRow={showAddNewRow}
              onCancelAddNew={() => setShowAddNewRow(false)}
            />
          </Modal.Body>
        </Modal>

        <Modal show={showNewGroup} onHide={toggleAddNewCategoryGroup}>
          {/* @ts-ignore - react-bootstrap 1.6.1 type definition issue */}
          <Modal.Header closeButton>
            <Modal.Title>Add New Category Group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Category Group Name</Form.Label>
                  <Form.Control
                    name="groupName"
                    value={groupName}
                    onChange={(e) => {
                      setGroupName(e.target.value);
                    }}
                    type="text"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={toggleAddNewCategoryGroup}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleAddNewCategoryGroup}
              className=""
            >
              <FontAwesomeIcon icon="plus" />
              <span
                className="ml-1"
                data-cy="add-new-category-group-modal-button"
              >
                Add New Category Group
              </span>
            </Button>
          </Modal.Footer>
        </Modal>
      </Row>
      <Row className="my-2 mx-1">
        <Col className="text-truncate" style={{ maxWidth: "800px" }}>
          <span className="font-weight-bold">Categories:</span>
          <span className="ml-2">{`${selectedTagIds
            .map((t) => getTagName(t))
            .join(",")}`}</span>
        </Col>
      </Row>
    </div>
  );
};
export default TagRegexes;
